import { ThunkDispatch } from 'redux-thunk'
import { Dispatch } from 'redux'
import { getItemFromLS, removeItemFromLS } from 'shared/helpers/LocalStorage'
import HTTPUrlHelper from 'shared/helpers/HTTPUrlHelper'
import { actionCoreApi, buildActionType } from 'core/utils/api'
import { IAppState } from 'core/store/store'
import HTTPClient from 'core/services/HTTPClient'
import { actionErrorNotifier } from 'core/utils/helpers'
import { IRouteParams, IVerificationFormData } from 'public-pages/verification/Verification.interfaces'
import { ISetupUserFormValues } from 'public-pages/setup-user/SetupUser.interfaces'
import { ISetupLicenseFormValues } from 'public-pages/license/License.interfaces'
import { fetchProfile } from './Profile.actions'
import { IAction } from './Actions.interfaces'
import ActionsList from './Actions.list'
import { ActionType } from '../utils/interfaces'
import {
  ACCESS_TOKEN_LS_KEY,
  ACCOUNT_EXPIRY_NOTIFICATION,
  LOCALES,
  PAGE_LEVEL_ERROR_NOTIFICATION,
  PAGE_LEVEL_WARN_NOTIFICATION,
  REFRESH_TOKEN_LS_KEY
} from 'core/utils/constants'
import { imauthLogoutUrl } from 'configs/Path.configs'
import { showNotification } from './Notifications.actions'
import { generateRandomId } from 'shared/helpers/utils'
import { NotificationTypes } from 'reducers/Reducers.interfaces'

export const setupUser = (formData: ISetupUserFormValues) =>
  actionErrorNotifier(actionCoreApi(
    ActionsList.SetupUser,
    HTTPUrlHelper.getSetupUserAPI(),
    'POST',
    formData
  ))

export const setupLicense = (formData: ISetupLicenseFormValues): ActionType =>
  actionErrorNotifier(actionCoreApi(
    ActionsList.SetupLicense,
    HTTPUrlHelper.getSetupLicenseAPI(),
    'POST',
    formData
  ))

export function logout () {
  const refreshTokenCookieName = getItemFromLS(REFRESH_TOKEN_LS_KEY) || ''
  const accessTokenCookieName = getItemFromLS(ACCESS_TOKEN_LS_KEY) || ''
  removeItemFromLS('activeSection')
  removeItemFromLS(ACCOUNT_EXPIRY_NOTIFICATION)
  removeItemFromLS(PAGE_LEVEL_ERROR_NOTIFICATION)
  removeItemFromLS(PAGE_LEVEL_WARN_NOTIFICATION)
  removeItemFromLS('account')
  removeItemFromLS(LOCALES)
  removeItemFromLS(refreshTokenCookieName)
  removeItemFromLS(accessTokenCookieName)
  window.location.href = `${imauthLogoutUrl}`
  return { type: ActionsList.AuthLogout }
}

export function setHubVisible () {
  return { type: ActionsList.SetHubVisible }
}

export function updateLicense (formData: ISetupLicenseFormValues) {
  return async (dispatch: ThunkDispatch<any, any, IAction>): Promise<void> => {
    dispatch({ type: ActionsList.UpdateLicenseRequest })
    const URL: string = HTTPUrlHelper.getUpdateLicenseAPI()
    const { data, status } = await HTTPClient.POST(URL, formData)
    if (status === 200) {
      dispatch({ type: ActionsList.UpdateLicenseSuccess })
      dispatch(showNotification({
        id: generateRandomId(),
        message: 'notifications.license.licenseUpdateTitle',
        type: NotificationTypes.success
      }))
    }
    if (data.error) {
      dispatch({ type: ActionsList.UpdateLicenseFailure })
      dispatch(showNotification({
        id: generateRandomId(),
        message: data.error.message ? `${data.error.message}` : `notifications.exceptions.${data.error.status}`,
        type: NotificationTypes.error
      }))
    }
  }
}

export function refreshToken () {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const URL: string = HTTPUrlHelper.getRefreshTokenAPI()
      // Refresh token is HttpOnly — send an empty body; the browser automatically
      // includes the cookie.  New tokens are returned as Set-Cookie headers by the
      // server, so no manual cookie manipulation is needed here.
      const { data, status } = await HTTPClient.POST(URL, {}, { withCredentials: true })

      if (data && status === 200) {
        dispatch({
          type: buildActionType(ActionsList.AuthToken, 'success'),
          payload: {
            expiresAt: data.expires_at
          }
        })
      } else if (status === 401 || status === 403) {
        dispatch(logout())
      }
      // any other status (5xx, network error) — silently ignore, next cycle will retry
    } catch {
      // silently ignore transient errors, next scheduled refresh will retry
    }
  }
}

export const getEnvironmentConfig = () =>
  actionErrorNotifier(actionCoreApi(
    ActionsList.EnvironmentConfig,
    HTTPUrlHelper.getEnvironmentConfigAPI(),
  ))


export const verifyUser = (formData: IVerificationFormData, routeParams: IRouteParams) =>
  actionErrorNotifier(actionCoreApi(
    ActionsList.VerifyUser,
    HTTPUrlHelper.getPasswordVerificationAPI(routeParams.userId),
    'POST',
    {
      verification_code: routeParams.verificationCode,
      login: formData.login,
      password: formData.password
    }
  ))

export function loginSuccess (redirectUrl: string, accessToken: string, expiresAt: string) {
  return async (dispatch: ThunkDispatch<IAppState, any, IAction>): Promise<void> => {
    dispatch({
      type: buildActionType(ActionsList.AuthToken, 'success'),
      payload: {
        accessToken: accessToken,
        expiresAt: expiresAt
      }
    })
    dispatch(fetchProfile())
  }
}
